import { useMemo, useState } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import { Modal, Portal, Text, useTheme } from 'react-native-paper'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { useUpdateActiveEntry } from '@/hooks/useUpdateActiveEntry'
import { BREAKPOINTS } from '@/lib/config'
import { parseDate } from '@/lib/dates'
import { captureException } from '@/lib/errors'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import { DismissKeyboardView } from '@/ui/DismissKeyboardView'
import { DateTimeInput, TextAreaInput, useForm, z, zodResolver } from '@/ui/form'

const editSchema = z.object({
    clockIn: z.date({ message: 'Start time is required' }),
    notes: z.string().trim().min(3, "Add a short note about what you're working on"),
})

type EditFormData = z.infer<typeof editSchema>

interface ActiveTimerEditSheetProps {
    visible: boolean
    entry: TimeEntriesExpanded
    onDismiss: () => void
}

export function ActiveTimerEditSheet({ visible, entry, onDismiss }: ActiveTimerEditSheetProps) {
    if (!visible) return null
    return <ActiveTimerEditSheetContent entry={entry} onDismiss={onDismiss} />
}

function ActiveTimerEditSheetContent({
    entry,
    onDismiss,
}: Omit<ActiveTimerEditSheetProps, 'visible'>) {
    const theme = useTheme()
    useDismissOnEscape(true, onDismiss)
    const updateActiveEntry = useUpdateActiveEntry()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg

    const defaultValues = useMemo(
        () => ({
            clockIn: parseDate(entry.clock_in),
            notes: entry.notes || '',
        }),
        [entry.clock_in, entry.notes]
    )

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<EditFormData>({
        resolver: zodResolver(editSchema),
        defaultValues,
        mode: 'onChange',
    })

    const onSubmit = async (data: EditFormData) => {
        setIsSubmitting(true)
        try {
            await updateActiveEntry.mutateAsync({
                entryId: entry.id,
                clock_in: data.clockIn.toISOString(),
                notes: data.notes || '',
            })
            onDismiss()
        } catch (error) {
            captureException('Edit active entry failed', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Portal>
            <Modal
                visible
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.modalContainer,
                    {
                        backgroundColor: theme.colors.surface,
                        width: isDesktop ? 400 : width - 40,
                    },
                ]}
            >
                <DismissKeyboardView>
                    <View style={styles.header}>
                        <Text variant="titleLarge">Edit Active Entry</Text>
                        <Button title="✕" onPress={onDismiss} mode="text" />
                    </View>
                    <View style={styles.content}>
                        <DateTimeInput
                            name="clockIn"
                            control={control}
                            label="Start Time"
                            mode="datetime"
                        />
                        <TextAreaInput
                            name="notes"
                            control={control}
                            label="What are you working on?"
                            placeholder="e.g. Framing south wall · Electrical rough-in · Punch list · Equipment maintenance"
                            numberOfLines={3}
                        />
                        <View style={styles.actions}>
                            <Button title="Cancel" onPress={onDismiss} />
                            <Button
                                title="Save"
                                onPress={handleSubmit(onSubmit)}
                                disabled={!isValid}
                                loading={isSubmitting}
                                loadingTitle="Saving..."
                            />
                        </View>
                    </View>
                </DismissKeyboardView>
            </Modal>
        </Portal>
    )
}

const styles = StyleSheet.create({
    modalContainer: {
        margin: 20,
        borderRadius: 12,
        alignSelf: 'center',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 24,
        paddingTop: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    },
    content: {
        paddingHorizontal: 24,
        paddingVertical: 16,
        gap: 16,
    },
    actions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 8,
    },
})
